// ============================================
// Revenue by Time Tab
// Table with CSS bars showing revenue over time periods
// ============================================

import { useState, useMemo } from 'react';
import type { TimeSeriesEntry } from './useFinancialData';

interface RevenueByTimeTabProps {
  byDay: TimeSeriesEntry[];
  byWeek: TimeSeriesEntry[];
  byMonth: TimeSeriesEntry[];
}

type Granularity = 'daily' | 'weekly' | 'monthly';

const GRANULARITY_OPTIONS: { label: string; value: Granularity }[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
];

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

export function RevenueByTimeTab({
  byDay,
  byWeek,
  byMonth,
}: RevenueByTimeTabProps) {
  const [granularity, setGranularity] = useState<Granularity>('daily');

  const data = useMemo(() => {
    let entries: TimeSeriesEntry[];
    switch (granularity) {
      case 'daily':
        entries = byDay;
        break;
      case 'weekly':
        entries = byWeek;
        break;
      case 'monthly':
        entries = byMonth;
        break;
    }
    // Sort by period descending (most recent first)
    return [...entries].sort((a, b) => b.period.localeCompare(a.period));
  }, [granularity, byDay, byWeek, byMonth]);

  const maxRevenue = useMemo(
    () => Math.max(...data.map((d) => d.revenueCents), 1),
    [data],
  );

  return (
    <div
      className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
    >
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 mb-5">
        {GRANULARITY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setGranularity(opt.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              granularity === opt.value
                ? 'bg-gold/15 text-gold'
                : 'bg-bg-elevated text-text-secondary hover:text-text-primary'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {data.length === 0 ? (
        <p className="text-text-muted text-base py-6 text-center">
          No revenue data for this period.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bg-tertiary">
                <th className="py-2 pr-4 text-sm font-medium text-text-muted uppercase tracking-wider">
                  Period
                </th>
                <th className="py-2 pr-4 text-sm font-medium text-text-muted uppercase tracking-wider text-right">
                  Orders
                </th>
                <th className="py-2 pr-4 text-sm font-medium text-text-muted uppercase tracking-wider text-right">
                  Revenue
                </th>
                <th className="py-2 text-sm font-medium text-text-muted uppercase tracking-wider hidden sm:table-cell">
                  &nbsp;
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry) => {
                const barPct = (entry.revenueCents / maxRevenue) * 100;
                return (
                  <tr
                    key={entry.period}
                    className="border-b border-bg-tertiary/50 last:border-0"
                  >
                    <td className="py-3 pr-4 text-base text-text-primary font-medium whitespace-nowrap">
                      {entry.period}
                    </td>
                    <td className="py-3 pr-4 text-base text-text-secondary text-right tabular-nums">
                      {entry.orderCount}
                    </td>
                    <td className="py-3 pr-4 text-base text-text-primary font-medium text-right tabular-nums whitespace-nowrap">
                      {formatCurrency(entry.revenueCents)}
                    </td>
                    <td className="py-3 hidden sm:table-cell w-1/3">
                      <div className="w-full bg-bg-elevated rounded h-2.5">
                        <div
                          className="bg-gold rounded h-2.5 transition-all duration-500"
                          style={{ width: `${Math.max(barPct, 2)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
